const DEV_SUFFIXES = ['localhost', '127.0.0.1'];
const PORTAL_PREFIXES = ['rider', 'merchant', 'customer'];
const SYSTEM_LABELS = new Set(['admin', 'www']);

function isDevHost(hostname = window.location.hostname) {
  const parts = hostname.split('.');
  return DEV_SUFFIXES.includes(parts[parts.length - 1]);
}

export function getBaseDomain(hostname = window.location.hostname) {
  if (isDevHost(hostname)) return 'localhost';
  const parts = hostname.split('.');
  if (parts.length >= 3) return parts.slice(-3).join('.');
  return hostname;
}

function parseFlatPortalLabel(label) {
  for (const prefix of PORTAL_PREFIXES) {
    const flatPrefix = `${prefix}-`;
    if (label.startsWith(flatPrefix)) {
      const slug = label.slice(flatPrefix.length);
      if (slug) return { portal: prefix, companySlug: slug };
    }
  }
  return null;
}

function parseLocalhost(hostname) {
  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return { portal: null, companySlug: null };
  }
  const suffix = host.endsWith('.localhost') ? '.localhost' : host.endsWith('.127.0.0.1') ? '.127.0.0.1' : null;
  if (!suffix) return null;

  const label = host.slice(0, -suffix.length);
  const parts = label.split('.');

  if (parts.length === 1) {
    if (parts[0] === 'admin') return { portal: 'admin', companySlug: null };
    const flat = parseFlatPortalLabel(parts[0]);
    if (flat) return flat;
    if (SYSTEM_LABELS.has(parts[0])) return null;
    return { portal: 'company', companySlug: parts[0] };
  }

  if (parts.length === 2 && PORTAL_PREFIXES.includes(parts[0])) {
    return { portal: parts[0], companySlug: parts[1] };
  }

  return null;
}

function parseDefaultDomain(hostname) {
  const baseDomain = getBaseDomain(hostname);
  if (isDevHost(hostname)) return null;

  const host = hostname.toLowerCase();
  const baseParts = baseDomain.split('.');
  const hostParts = host.split('.');
  if (hostParts.slice(-baseParts.length).join('.') !== baseDomain) return null;

  const prefixParts = hostParts.slice(0, -baseParts.length);
  if (prefixParts.length === 0) return { portal: null, companySlug: null };

  if (prefixParts.length === 1) {
    const label = prefixParts[0];
    if (label === 'admin') return { portal: 'admin', companySlug: null };
    if (SYSTEM_LABELS.has(label)) return null;
    const flat = parseFlatPortalLabel(label);
    if (flat) return flat;
    return { portal: 'company', companySlug: label };
  }

  if (prefixParts.length === 2 && PORTAL_PREFIXES.includes(prefixParts[0])) {
    return { portal: prefixParts[0], companySlug: prefixParts[1] };
  }

  return null;
}

/** Full host context: portal + company slug */
export function parseHostContext(hostname = window.location.hostname) {
  const local = parseLocalhost(hostname);
  if (local) return local;

  const parsed = parseDefaultDomain(hostname);
  if (parsed) return parsed;

  return { portal: null, companySlug: null };
}

export function getPortal() {
  return parseHostContext().portal;
}

export function getCompanySlug() {
  return parseHostContext().companySlug;
}

/** Returns 'admin' or company slug — backward compatible */
export function detectSubdomain() {
  const ctx = parseHostContext();
  if (ctx.portal === 'admin') return 'admin';
  return ctx.companySlug;
}

export function isSubdomainEnvironment() {
  return parseHostContext().portal !== null;
}

export function getSubdomainBaseUrl(slug) {
  return getPortalUrl(slug, 'company');
}

/** Single-level subdomains: rider-{slug}.domain (wildcard SSL friendly) */
export function defaultPortalHosts(slug) {
  const s = String(slug || '').toLowerCase();
  const domain = isDevHost() ? 'localhost' : getBaseDomain();

  return {
    company: `${s}.${domain}`,
    rider: `rider-${s}.${domain}`,
    merchant: `merchant-${s}.${domain}`,
    customer: `customer-${s}.${domain}`,
  };
}

export function defaultPortalHostsFlat(slug) {
  return defaultPortalHosts(slug);
}

export function getPortalUrl(slug, portal, customHosts = null) {
  if (!slug && portal !== 'admin') return null;

  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  const domain = isDevHost() ? 'localhost' : getBaseDomain();

  if (customHosts) {
    const key = `domain_${portal}`;
    if (customHosts[key]) return `${protocol}//${customHosts[key]}${port}`;
  }

  if (portal === 'admin') return `${protocol}//admin.${domain}${port}`;
  if (portal === 'company') return `${protocol}//${slug}.${domain}${port}`;
  return `${protocol}//${portal}-${slug}.${domain}${port}`;
}

export function getPortalUrlFlat(slug, portal, customHosts = null) {
  return getPortalUrl(slug, portal, customHosts);
}
